import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Paperclip } from 'lucide-react';
import { useCollaborations } from '../context/CollaborationsContext';
import { apiFetch, apiDownload, apiUpload } from '../api';
import AttachmentRow from './AttachmentRow';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

// Mismos topes que en server/routes/adjuntosColaboracion.js y
// server/lib/adjuntosSolicitud.js, que son los que mandan. Si cambias uno,
// cambia el otro.
const MAX_ARCHIVOS = 10;
const MAX_SUBIDA_MB = 20;
const EXTENSIONES = ['.pdf', '.doc', '.docx'];
const ACEPTA =
  '.pdf,.doc,.docx,application/pdf,application/msword,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// Los guardados antes de existir `origen` son los que trajo el influencer.
const esDelEquipo = (adjunto) => adjunto.origen === 'equipo';

function extension(nombre) {
  const punto = nombre.lastIndexOf('.');
  return punto === -1 ? '' : nombre.slice(punto).toLowerCase();
}

/** Motivo por el que no se puede subir esta selección, o '' si vale. */
function problemaDeSeleccion(archivos, ocupados) {
  const malo = archivos.find((a) => !EXTENSIONES.includes(extension(a.name || '')));
  if (malo) return `"${malo.name}" no es un PDF ni un Word: solo se admiten PDF o Word (.doc o .docx)`;
  const huecos = MAX_ARCHIVOS - ocupados;
  if (archivos.length > huecos) {
    return huecos === 1
      ? `Solo queda 1 hueco: esta colaboración tiene ${ocupados} de ${MAX_ARCHIVOS} archivos`
      : `Solo quedan ${huecos} huecos: esta colaboración tiene ${ocupados} de ${MAX_ARCHIVOS} archivos`;
  }
  const total = archivos.reduce((suma, a) => suma + a.size, 0);
  if (total > MAX_SUBIDA_MB * 1024 * 1024) {
    return `Los archivos no pueden pasar de ${MAX_SUBIDA_MB} MB entre todos`;
  }
  return '';
}

function Lista({ titulo, adjuntos, onDescargar, onBorrar, borrando }) {
  if (adjuntos.length === 0) return null;
  return (
    <div className="space-y-2">
      <div className="text-[11px] font-medium text-muted-foreground">{titulo}</div>
      {adjuntos.map((adjunto) => (
        <AttachmentRow
          key={adjunto.id}
          attachment={{ filename: adjunto.nombre }}
          iconClass="text-muted-foreground"
          busy={borrando === adjunto.id}
          onDownload={() => onDescargar(adjunto)}
          onDelete={() => onBorrar(adjunto)}
        />
      ))}
    </div>
  );
}

/**
 * Los archivos de una colaboración dentro de «Gestionar»: los que sube el
 * equipo y los que trajo el influencer del formulario. Entre todos, como mucho
 * 10. Adjuntar es opcional: sin archivos la colaboración se gestiona igual.
 */
export default function ArchivosColaboracion({ collab }) {
  const { applyCollaboration } = useCollaborations();
  const entrada = useRef(null);
  const [subiendo, setSubiendo] = useState(false);
  const [borrando, setBorrando] = useState(null);

  const adjuntos = collab.adjuntos || [];
  const total = adjuntos.length;
  const lleno = total >= MAX_ARCHIVOS;

  async function subir(e) {
    const archivos = Array.from(e.target.files || []);
    // Se vacía ya: así elegir otra vez el mismo archivo vuelve a disparar el cambio.
    e.target.value = '';
    if (archivos.length === 0) return;

    const problema = problemaDeSeleccion(archivos, total);
    if (problema) {
      toast.error(problema);
      return;
    }

    const formulario = new FormData();
    archivos.forEach((archivo) => formulario.append('adjuntos', archivo, archivo.name));
    setSubiendo(true);
    try {
      const { collaboration } = await apiUpload(`/api/collaborations/${collab.id}/adjuntos`, formulario);
      applyCollaboration(collaboration);
      toast.success(archivos.length === 1 ? 'Archivo adjuntado' : `${archivos.length} archivos adjuntados`);
    } catch (err) {
      toast.error(`No se pudo adjuntar: ${err.message}`);
    } finally {
      setSubiendo(false);
    }
  }

  async function descargar(adjunto) {
    try {
      await apiDownload(`/api/collaborations/${collab.id}/adjuntos/${adjunto.id}`, adjunto.nombre);
    } catch (err) {
      toast.error(`No se pudo descargar: ${err.message}`);
    }
  }

  async function borrar(adjunto) {
    const deDonde = esDelEquipo(adjunto)
      ? ''
      : '\n\nLo subió el influencer: la solicitud original lo conserva.';
    if (!window.confirm(`¿Borrar "${adjunto.nombre}" de esta colaboración?${deDonde}`)) return;
    setBorrando(adjunto.id);
    try {
      const { collaboration } = await apiFetch(`/api/collaborations/${collab.id}/adjuntos/${adjunto.id}`, {
        method: 'DELETE',
      });
      applyCollaboration(collaboration);
      toast.success(`"${adjunto.nombre}" borrado`);
    } catch (err) {
      toast.error(`No se pudo borrar: ${err.message}`);
    } finally {
      setBorrando(null);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Archivos</div>
        <span
          className={cn('text-[11px] tabular-nums', lleno ? 'text-destructive font-medium' : 'text-muted-foreground')}
          title="Archivos del equipo y del influencer"
        >
          {total}/{MAX_ARCHIVOS}
        </span>
      </div>

      <Button
        variant="outline"
        size="sm"
        className="h-8 w-full gap-1.5 px-2.5"
        disabled={subiendo || lleno}
        onClick={() => entrada.current?.click()}
      >
        <Paperclip className="h-3.5 w-3.5" />
        {subiendo ? 'Enviando…' : 'Adjuntar archivos'}
      </Button>
      {lleno && (
        <p className="text-[11px] text-muted-foreground">
          {total === MAX_ARCHIVOS
            ? `Ya hay ${MAX_ARCHIVOS} archivos. Borra alguno para subir más.`
            : `Hay ${total} archivos, más de ${MAX_ARCHIVOS}. Borra alguno para bajar de ${MAX_ARCHIVOS} y poder subir más.`}
        </p>
      )}
      <input
        ref={entrada}
        type="file"
        multiple
        className="file-upload-input"
        accept={ACEPTA}
        onChange={subir}
      />

      <Lista
        titulo="Archivos del equipo"
        adjuntos={adjuntos.filter(esDelEquipo)}
        onDescargar={descargar}
        onBorrar={borrar}
        borrando={borrando}
      />
      <Lista
        titulo="Archivos del influencer"
        adjuntos={adjuntos.filter((a) => !esDelEquipo(a))}
        onDescargar={descargar}
        onBorrar={borrar}
        borrando={borrando}
      />
    </div>
  );
}
